import { useMemo, useState } from 'react'
import type { EChartsCoreOption } from 'echarts/core'
import {
  formatMoney,
  formatPercent,
  type AllocationDimension,
  type AllocationReport,
} from '@greencandle/api-client'
import { useECharts } from './echarts'
import { MAX_SERIES, useChartTheme, type ChartTheme } from './theme'

const DIMENSIONS: { value: AllocationDimension; label: string }[] = [
  { value: 'ASSET_CLASS', label: 'Class' },
  { value: 'SECTOR', label: 'Sector' },
  { value: 'CURRENCY', label: 'Currency' },
]

/**
 * FR-E4, drawn as a horizontal stacked bar rather than a donut.
 *
 * Part-to-whole at a glance is what a donut is for, and it is bad at the one thing this
 * data does constantly: comparing two shares that are close. 56% and 44% are two arcs
 * nobody can rank by eye, where the same two segments on a bar are immediately ordered.
 * A bar also survives long category names, which sector labels are.
 */
export function AllocationBar({
  report,
  dimension,
  onDimensionChange,
  dimmed,
}: {
  report: AllocationReport
  dimension: AllocationDimension
  onDimensionChange: (next: AllocationDimension) => void
  dimmed: boolean
}) {
  const theme = useChartTheme()
  const [showTable, setShowTable] = useState(false)

  // Colour follows the bucket's position in the server's fixed ordering, so switching
  // dimension or filtering never repaints the buckets that survive.
  const shown = report.buckets.slice(0, MAX_SERIES)
  const hidden = report.buckets.length - shown.length

  const option = useMemo<EChartsCoreOption | null>(() => {
    if (shown.length === 0) {
      return null
    }

    const last = shown.length - 1
    return {
      animation: false,
      grid: { left: 0, right: 0, top: 0, bottom: 0 },
      xAxis: { type: 'value', show: true, max: 'dataMax', axisLabel: { show: false }, splitLine: { show: false }, axisLine: { show: false }, axisTick: { show: false } },
      yAxis: { type: 'category', data: [''], show: false },
      tooltip: {
        // On a bar the mark is the hit target: no crosshair, each segment answers for
        // itself.
        trigger: 'item',
        backgroundColor: theme.surface,
        borderColor: theme.grid,
        textStyle: { color: theme.text },
        formatter: (params: { seriesName?: string; dataIndex?: number }) => {
          const bucket = shown.find((b) => b.key === params.seriesName)
          if (!bucket) {
            return ''
          }
          const root = document.createElement('div')

          const value = document.createElement('div')
          value.style.cssText = 'font-weight:600;font-size:15px'
          value.textContent = formatMoney(bucket.value, report.currency) ?? ''
          root.append(value)

          const name = document.createElement('div')
          name.style.cssText = `color:${theme.textMuted};font-size:12px;margin-top:2px`
          // Bucket keys are account names and sector text - user-supplied. textContent,
          // never string-built markup.
          name.textContent = `${bucket.key} · ${formatPercent(bucket.weightPercent) ?? '—'} · ${bucket.holdings} holding${bucket.holdings === 1 ? '' : 's'}`
          root.append(name)

          return root
        },
      },
      series: shown.map((bucket, index) => ({
        name: bucket.key,
        type: 'bar',
        stack: 'allocation',
        barWidth: 24,
        // Geometry only - every figure on screen is formatted from the server's string.
        data: [Number(bucket.value)],
        itemStyle: {
          color: colorFor(theme, index, bucket.key),
          // A 1px ring in the surface colour on each segment puts a 2px gap between
          // neighbours. White does the separating; no data-weight stroke is added.
          borderColor: theme.surface,
          borderWidth: 1,
          // The stack's two data-ends are rounded; the interior joins stay square.
          borderRadius:
            shown.length === 1
              ? 4
              : index === 0
                ? [4, 0, 0, 4]
                : index === last
                  ? [0, 4, 4, 0]
                  : 0,
        },
        label: {
          // Only where it fits. A share under a sixth of the bar cannot hold its own
          // name at any sensible width, and a clipped label is worse than none - the
          // legend below and the table carry every value regardless.
          show: (Number(bucket.weightPercent ?? 0) >= 16),
          position: 'inside',
          formatter: () => bucket.key,
          color: inkOn(colorFor(theme, index, bucket.key)),
          fontSize: 12,
          fontWeight: 600,
        },
      })),
    }
  }, [shown, report.currency, theme])

  const container = useECharts(option)

  return (
    <div className="card">
      <div className="chart-head">
        <h2 className="card-title">Allocation</h2>
        <div className="segmented" role="group" aria-label="Group allocation by">
          {DIMENSIONS.map((d) => (
            <button
              key={d.value}
              type="button"
              aria-pressed={dimension === d.value}
              onClick={() => onDimensionChange(d.value)}
            >
              {d.label}
            </button>
          ))}
        </div>
        <div className="chart-head-end">
          <button type="button" onClick={() => setShowTable((v) => !v)} aria-pressed={showTable}>
            {showTable ? 'Chart' : 'Table'}
          </button>
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="empty-chart">
          {report.note ?? 'Nothing could be valued, so there is no allocation to show.'}
        </div>
      ) : showTable ? (
        <AllocationTable report={report} />
      ) : (
        <div className={dimmed ? 'refetching' : undefined}>
          <div ref={container} style={{ width: '100%', height: 40 }} />

          {/*
            A legend is always present for two or more series, and it carries the values
            rather than only the colours: identity never rests on colour-matching, and the
            figures are readable without hovering anything.
          */}
          <div className="legend">
            {shown.map((bucket, index) => (
              <span className="legend-item" key={bucket.key}>
                <span
                  className="legend-swatch"
                  style={{ background: colorFor(theme, index, bucket.key) }}
                  aria-hidden="true"
                />
                {bucket.key}
                <span className="legend-value">
                  {formatPercent(bucket.weightPercent) ?? '—'}
                </span>
              </span>
            ))}
          </div>

          {hidden > 0 && (
            <p className="chart-note warn">
              Showing the {MAX_SERIES} largest of {report.buckets.length}. The rest are in
              the table.
            </p>
          )}
          {report.note && <p className="chart-note muted">{report.note}</p>}
          {report.unvalued.length > 0 && (
            <p className="chart-note muted">
              {/*
                Capped. An option book contributes eighteen 21-character OCC symbols, and the
                full list buried the chart under a wall of identifiers nobody reads. The count
                is the useful part; the table view carries every one with its reason.
              */}
              Not counted: {report.unvalued.slice(0, 4).map((u) => u.symbol).join(', ')}
              {report.unvalued.length > 4
                ? ` and ${report.unvalued.length - 4} more — see the table`
                : ''}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function AllocationTable({ report }: { report: AllocationReport }) {
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Bucket</th>
            <th className="right">Value</th>
            <th className="right">Share</th>
            <th className="right">Holdings</th>
          </tr>
        </thead>
        <tbody>
          {report.buckets.map((bucket) => (
            <tr key={bucket.key}>
              <td>{bucket.key}</td>
              <td className="right num">{formatMoney(bucket.value, report.currency)}</td>
              <td className="right num">{formatPercent(bucket.weightPercent) ?? '—'}</td>
              <td className="right num">{bucket.holdings}</td>
            </tr>
          ))}
          {report.unvalued.map((u) => (
            <tr key={`${u.account}-${u.symbol}`}>
              <td>
                {u.symbol} <span className="pill">not valued</span>
              </td>
              <td className="right muted" colSpan={3}>
                {u.reason}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function colorFor(theme: ChartTheme, index: number, key: string): string {
  if (key === 'UNCLASSIFIED' || index >= theme.series.length) {
    // Unclassified is the absence of a category, not a category. It takes the neutral so
    // it never reads as a sector with a name.
    return theme.other
  }
  return theme.series[index] ?? theme.other
}

/**
 * White or ink, whichever actually has more contrast against the fill the label sits on.
 *
 * Computed rather than guessed at with a lightness threshold. A mid-luminance fill like the
 * slot-2 orange is the case that catches people out: it looks dark enough for white text,
 * but white on it is 3.6:1 where black is 5.8:1, and a 12px bold label needs 4.5:1.
 */
function inkOn(fill: string): string {
  const hex = fill.replace('#', '')
  if (hex.length !== 6) {
    return '#ffffff'
  }
  const channel = (start: number) => {
    const v = parseInt(hex.slice(start, start + 2), 16) / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4)
  const againstWhite = 1.05 / (luminance + 0.05)
  const againstInk = (luminance + 0.05) / 0.05
  return againstInk >= againstWhite ? '#0b0b0b' : '#ffffff'
}
